import { Fragment, useEffect, useMemo, useState } from "react";
import UserNotificationMediumTable from "../../components/NotificationsScreen/UserNotificationMediumTable";
import HeaderColumnData from "../../services/models/notificationsSettingsTableModels/headerColumnData.model";
import BodyColumnData from "../../components/NotificationsScreen/BodyColumnData.model";
import NotificationSettingsTableLoadingIndicator from "../../components/global/LoadingIndicators/NotificationsSettingsTableLoadingIndicator";
import UserNotificationTypeTable from "../../components/NotificationsScreen/UserNotificationTypeTable";
import { useHistory } from "react-router-dom";
import RouteLeavingGuard from "./RouteLeaving";
import SubHeader from "../../components/global/SubHeader";
import updateTypeSettingsPayloadItem from "../../services/models/notificationsModels/updateTypeSettingsPayloadItem";
import UpdateOrganizationSettingsPayloadItem from "../../services/models/notificationsModels/updateOrganizationSettingsPayloadItem";
import toastMessages from "../../services/utils/helpers/toastMessage";
import { useOktaAuth } from "@okta/okta-react";
import axiosInstance from "../../services/api";
import useUserMediumValues from "../../services/utils/custom hooks/Notifications/useUserMediumValues";
import useUserNotificationTypeValues from "../../services/utils/custom hooks/Notifications/useUserNotificationTypeValues";
import useNotificationTypes from "../../services/utils/custom hooks/Notifications/useNotificationTypes";
import _ from "lodash"; // Import lodash for deep cloning
import useProfile from "../../services/utils/custom hooks/useProfile";

interface PersonalSettingsProps {
  fetching: boolean;
  error: boolean;
}

function PersonalSettings({ fetching, error }: PersonalSettingsProps) {
  const [tableColumns, setTableColumns] = useState<HeaderColumnData[]>([]);
  const { authState } = useOktaAuth();
  const [tableBody, setTableBody] = useState<Array<BodyColumnData[]>>([]);
  const [
    userNotificationMediumTableColumn,
    setUserNotificationMediumTableColumn,
  ] = useState<HeaderColumnData[]>([]);
  const [userNotificationMediumTableBody, setUserNotificationMediumTableBody] =
    useState<Array<BodyColumnData[]>>([]);
  const [
    initialUserNotificationMediumTableBody,
    setInitialUserNotificationMediumTableBody,
  ] = useState<Array<BodyColumnData[]>>([]);
  const [
    initialUserNotificationTypeTableBody,
    setInitialUserNotificationTypeTableBody,
  ] = useState<Array<BodyColumnData[]>>([]);
  const [saving, setSaving] = useState<boolean>(false);
  const history = useHistory();
  const { data: profile } = useProfile();
  const isPhysician = profile?.user?.roles?.includes("Physician");
  const { refetch: refetchUserMediumValues } = useUserMediumValues({
    enabled: !!profile && isPhysician,
  });
  const { refetch: refetchUserTypeValues } = useUserNotificationTypeValues();
  const { refetch: refetchNotificationTypes } = useNotificationTypes();

  useEffect(() => {
    if (
      initialUserNotificationMediumTableBody.length === 0 &&
      userNotificationMediumTableBody.length > 0
    ) {
      setInitialUserNotificationMediumTableBody(
        _.cloneDeep(userNotificationMediumTableBody)
      );
    }
  }, [userNotificationMediumTableBody]);

  useEffect(() => {
    if (
      initialUserNotificationTypeTableBody.length === 0 &&
      tableBody.length > 0
    ) {
      setInitialUserNotificationTypeTableBody(_.cloneDeep(tableBody));
    }
  }, [tableBody]);

  const isPersonalSettingsChanged = useMemo(() => {
    return (
      JSON.stringify(userNotificationMediumTableBody) !==
        JSON.stringify(initialUserNotificationMediumTableBody) ||
      JSON.stringify(tableBody) !==
        JSON.stringify(initialUserNotificationTypeTableBody)
    );
  }, [
    userNotificationMediumTableBody,
    initialUserNotificationMediumTableBody,
    tableBody,
    initialUserNotificationTypeTableBody,
  ]);

  async function handleSave() {
    const typeSettingsMap = new Map<string, updateTypeSettingsPayloadItem>();
    const mediumSettingsMap = new Map<
      string,
      UpdateOrganizationSettingsPayloadItem
    >();

    tableBody.forEach((data) => {
      data.forEach((value) => {
        if (Array.isArray(value.data)) {
          let organizationId = !Array.isArray(data[0].data)
            ? Number(data[0].data.id)
            : 0;
          value.data.forEach((setting) => {
            let settingItem: updateTypeSettingsPayloadItem = {
              organizationId,
              notificationTypeId: Number(setting.id),
              value: typeof setting.value === "boolean" ? setting.value : false,
            };
            const typeKey = `${organizationId}-${setting.id}`;
            typeSettingsMap.set(typeKey, settingItem);
          });
        }
      });
    });

    // Only include medium data if the user is a Physician
    if (profile?.user?.roles?.includes("Physician")) {
      userNotificationMediumTableBody.forEach((data) => {
        data.forEach((value) => {
          if (Array.isArray(value.data)) {
            let organizationId = !Array.isArray(data[0].data)
              ? Number(data[0].data.id)
              : 0;
            value.data.forEach((setting) => {
              let setting2item: UpdateOrganizationSettingsPayloadItem = {
                notificationMediumId: Number(setting.id),
                value:
                  typeof setting.value === "boolean" ? setting.value : false,
              };
              const mediumKey = `${organizationId}-${setting.id}`;
              mediumSettingsMap.set(mediumKey, setting2item);
            });
          }
        });
      });
    }

    const apiData = Array.from(typeSettingsMap.values());
    const apiMediumData = Array.from(mediumSettingsMap.values());

    const body = { data: apiData };
    const body2 = { data: apiMediumData };

    setSaving(true);

    try {
      const config = {
        headers: {
          Authorization: `Bearer ${authState?.accessToken?.accessToken}`,
        },
      };

      const [typeResponse, mediumResponse] = await Promise.all([
        axiosInstance.post("v1/user-notification-type-settings", body, config),
        profile?.user?.roles?.includes("Physician")
          ? axiosInstance.post("v1/user-notification-mediums", body2, config)
          : Promise.resolve({ status: 200 }),
      ]);

      if (typeResponse.status === 200 && mediumResponse.status === 200) {
        setInitialUserNotificationMediumTableBody(
          userNotificationMediumTableBody
        );
        setInitialUserNotificationTypeTableBody(tableBody);

        await Promise.all([
          refetchNotificationTypes(),
          refetchUserMediumValues(),
          refetchUserTypeValues(),
        ]);

        setSaving(false);
        toastMessages.success("Settings saved successfully.");
      } else {
        throw new Error("Failed to save settings.");
      }
    } catch (error: any) {
      setSaving(false);
      toastMessages.error(
        error.message || "An error occurred while saving settings."
      );
    }
  }

  return (
    <Fragment>
      <RouteLeavingGuard
        when={isPersonalSettingsChanged}
        navigate={(path) => {
          history.push(path);
        }}
        shouldBlockNavigation={() => {
          if (isPersonalSettingsChanged) {
            return true;
          }
          return false;
        }}
      />
      <SubHeader
        title="Personal Settings"
        hideSubHeaderSelectors
        showButtonInSubheaderForMobile
        containerClass="md:flex"
        customButtons={[
          {
            textInformation: {
              text: "save",
              fontWeight: "font-semibold",
            },
            onClick: () => handleSave(),
            buttonTheme: "primary",
            disabled: saving,
            className: "my-2 mx-5",
            loading: saving,
          },
        ]}
      />
      <div>
        {/* Conditionally render UserNotificationMediumTable */}
        {profile?.user?.roles?.includes("Physician") && (
          <div className="px-6 !overflow-auto">
            <p className="text-royalBlue font-bold text-xl pb-6 py-8">
              Notification Medium / Report Delivery
            </p>
            {saving || fetching ? (
              <NotificationSettingsTableLoadingIndicator />
            ) : (
              <UserNotificationMediumTable
                tableBody={userNotificationMediumTableBody}
                tableColumns={userNotificationMediumTableColumn}
                setTableBody={setUserNotificationMediumTableBody}
                setTableColumns={setUserNotificationMediumTableColumn}
                fetching={false}
                error={false}
              />
            )}
          </div>
        )}
        <div className="px-6 pt-8 pb-12">
          <p className="text-royalBlue font-bold text-xl pb-6 ">
           Portal Notification Types
          </p>
          <div className="overflow-scroll">
            {saving || fetching ? (
              <NotificationSettingsTableLoadingIndicator />
            ) : (
              <UserNotificationTypeTable
                tableBody={tableBody}
                tableColumns={tableColumns}
                setTableBody={setTableBody}
                setTableColumns={setTableColumns}
                fetching={false}
                error={false}
                userNotificationTypesValues={[]}
              />
            )}
          </div>
        </div>
      </div>
    </Fragment>
  );
}

export default PersonalSettings;
